import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, till } from "@/lib/api";
import { date, money, personName } from "@/lib/format";
import { Button, Input, Modal, Pager, usePaged } from "@/components/ui";
import { Card, PageHead } from "@/components/Layout";
import { SupplierModal, useSuppliers, type Supplier } from "@/components/SupplierBits";

type Delivery = { id: string; itemName: string | null; qty: number; cost: number | null; ref: string | null; by: string | null; at: number };

function Deliveries({ s, onClose, onEdit }: { s: Supplier; onClose: () => void; onEdit: () => void }) {
  const q = useQuery({ queryKey: ["supplier-deliveries", s.id], queryFn: () => api<{ deliveries: Delivery[] }>(till(`/admin/suppliers/${s.id}/deliveries`)) });
  const rows = q.data?.deliveries ?? [];
  return (
    <Modal wide title={s.name} sub={[s.contact, s.phone].filter(Boolean).join(" · ") || "No contact details yet"} onClose={onClose} footer={<>{s.phone ? <a href={`tel:${s.phone}`} className="inline-flex h-11 items-center border border-line-2 bg-white px-4 text-sm font-bold">Call</a> : null}<Button onClick={onEdit}>Edit</Button></>}>
      {rows.map((d) => (
        <div key={d.id} className="flex justify-between gap-3 border-b border-slate-100 py-2.5 text-sm">
          <span className="min-w-0"><b className="block font-semibold">{d.itemName ?? "Removed product"}</b><span className="text-xs text-slate-500">{date(d.at)} · {d.qty.toLocaleString("en-GB")} received{d.ref ? ` · ${d.ref}` : ""}{d.by ? ` · ${personName(d.by)}` : ""}</span></span>
          <b className="flex-none">{d.cost == null ? "No cost" : money(d.cost)}</b>
        </div>
      ))}
      {q.data && !rows.length ? <p className="py-6 text-center text-sm text-slate-500">No deliveries from this supplier yet.</p> : null}
    </Modal>
  );
}

/** Who stock is bought from, and what came from each of them. */
export function Suppliers({ embedded }: { embedded?: boolean }) {
  const q = useSuppliers();
  const [find, setFind] = useState("");
  const [open, setOpen] = useState<Supplier | null>(null);
  const [edit, setEdit] = useState<Supplier | "new" | null>(null);
  const words = find.trim().toLowerCase();
  const list = (q.data?.suppliers ?? []).filter((s) => !words || [s.name, s.phone, s.contact].some((v) => v?.toLowerCase().includes(words)));
  const paged = usePaged(list);
  return (
    <>
      {embedded ? <div className="mb-3 flex justify-end"><Button tone="primary" onClick={() => setEdit("new")}>Add supplier</Button></div> : <PageHead title="Suppliers" sub="Who you buy stock from. Each delivery is recorded against its supplier."><Button tone="primary" onClick={() => setEdit("new")}>Add supplier</Button></PageHead>}
      <Card>
        <div className="p-4"><Input aria-label="Find a supplier" value={find} onChange={(e) => setFind(e.target.value)} placeholder="Find a supplier" className="sm:!w-72" /></div>
        {paged.rows.map((s) => (
          <button key={s.id} type="button" onClick={() => setOpen(s)} className="flex min-h-14 w-full items-center justify-between gap-3 !rounded-none border-t border-slate-100 px-4 py-2.5 text-left text-sm hover:bg-slate-50">
            <span className="min-w-0"><b className="block font-semibold">{s.name}</b><span className="text-xs text-slate-500">{[s.contact, s.phone].filter(Boolean).join(" · ") || "No contact details yet"}</span></span>
            <span className="flex-none text-right text-xs text-slate-500"><b className="block text-sm font-semibold text-ink">{s.deliveries} deliver{s.deliveries === 1 ? "y" : "ies"}</b>{s.lastAt ? `Last: ${date(s.lastAt)}` : "None yet"}</span>
          </button>
        ))}
        {q.data && !list.length ? <p className="border-t border-slate-100 px-4 py-8 text-center text-sm text-slate-500">{find ? "No supplier matches." : "No suppliers yet. Add your first one."}</p> : null}
        <Pager page={paged.page} size={paged.size} total={paged.total} onPage={paged.setPage} />
      </Card>
      {open ? <Deliveries s={open} onClose={() => setOpen(null)} onEdit={() => { setEdit(open); setOpen(null); }} /> : null}
      {edit ? <SupplierModal person={edit === "new" ? null : edit} onClose={() => setEdit(null)} /> : null}
    </>
  );
}
